import { useDispatch, useSelector } from 'react-redux';
import { FormikContextType } from 'formik';
import { TFunction } from 'i18next';
import { ObjectSchema } from 'yup';
import {
  BooleanEnum,
  CustomerDetailsSection,
  CustomerType,
  KnownSearchParams,
} from '../../../resources/base/models/enums';
import { routes } from '../../../routing/routes';
import { Analytics } from '../../../services/analytics';
import { applicationFormEvent } from '../../../services/analytics/analyticsEvents';
import {
  createCreditApplication,
  getCreditApplication,
  getCreditApplicationCustomerData,
  patchCreditApplicationCustomerData,
} from '../../../services/api/miles/miles-credit-services';
import { getStakeholderContacts as getCustomerStakeholderContacts } from '../../../services/api/miles/miles-customer-services';
import { getQuoteStakeholderContacts } from '../../../services/api/miles/miles-services';
import { CreateCreditApplicationRequest } from '../../../services/api/miles/types';
import { getTransactionId } from '../../../services/common/data';
import { assertDefined, assertDefinedVoid, getEnumValues, waitForMiles } from '../../../services/common/miscUtils';
import { DeepPartial } from '../../../services/common/miscUtils.types';
import { Yup } from '../../../services/common/validation';
import { staticSelector, store } from '../../../services/redux';
import {
  creditApplicationSelectors,
  setCreditApplicantCustomerData,
  setCreditApplicantCustomerDataCoCustomer,
  setCreditApplicationDetails,
  setExpandedForm,
  setFormAsNotSubmitted,
  setFormAsSubmitted,
  setIsCreditApplicationDataReady,
} from '../../../services/redux/features/creditApplication.slice';
import {
  customerSelectors,
  setAvailableSignatories,
  setIsSignatoryFormActive,
  setShouldLinkDirectorStakeholder,
  setShouldLinkSignatoryStakeholder,
  setStakeholderContact,
} from '../../../services/redux/features/customer.slice';
import { IdentData, selectDataStorage, setDataStorageNew } from '../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { quoteSelectors, setStakeholderContacts } from '../../../services/redux/features/quote.slice';
import { uiDateToApiDate } from '../utils/formatDate';
import { getStakeholderInfoDataFormInitialValues } from './businessCustomerDetailsForms/initialValues';
import { CompanyDataFormType } from './privateCustomerDetailsForms/initialValues';

/**
 * Scrolls to the top of a given section smoothly.
 * @param customerDetailsSection - The section to scroll to.
 */
const scrollToSectionTop = (customerDetailsSection: CustomerDetailsSection): void => {
  setTimeout(
    () => {
      const currentSection = document.getElementById(customerDetailsSection);
      currentSection?.scrollIntoView();
    },
    /**
     * - Arbitrary timeout allowing previous focused section to collapse.
     * - Focusing when page geometry still changes will give faulty results.
     */
    650
  );
};

/**
 * - Customer details page forms for each customer in order of appearance.
 */
export const formGroups = {
  private: [
    CustomerDetailsSection.CONTACT_INFO,
    CustomerDetailsSection.ADDRESS_INFO,
    CustomerDetailsSection.FINANCIAL_INFO,
    CustomerDetailsSection.PAYMENT_INFO,
    CustomerDetailsSection.COMPANY_INFO,
  ],
  coCustomer: [
    CustomerDetailsSection.CONTACT_COCO_INFO,
    CustomerDetailsSection.ADDRESS_COCO_INFO,
    CustomerDetailsSection.FINANCIAL_COCO_INFO,
    CustomerDetailsSection.COMPANY_COCO_INFO,
  ],
  business: [
    CustomerDetailsSection.GENERAL_INFO,
    CustomerDetailsSection.ADDRESS_BUSINESS_INFO,
    CustomerDetailsSection.EXECUTIVE_INFO,
    CustomerDetailsSection.SIGNATORY_INFO,
    CustomerDetailsSection.PAYMENT_INFO,
    CustomerDetailsSection.COMPANY_INFO,
  ],
  soleTrader: [
    CustomerDetailsSection.GENERAL_INFO,
    CustomerDetailsSection.ADDRESS_BUSINESS_INFO,
    CustomerDetailsSection.PERSONAL_INFO,
    CustomerDetailsSection.ADDITIONAL_INFO,
    CustomerDetailsSection.PAYMENT_INFO,
    CustomerDetailsSection.COMPANY_INFO,
  ],
} as const;

export type CustomerDetailsFormGroupType = keyof typeof formGroups;
export type CustomerDetailsFormGroupForms<T extends CustomerDetailsFormGroupType> = (typeof formGroups)[T][number];

export type DisabledCreditApplicationBirthInformationFields = {
  nationality: boolean;
  birthName?: boolean;
  birthCountry: boolean;
  zipCodeOfBirth: boolean;
  placeOfBirth: boolean;
};

const getFirstNotSubmittedForm = <T extends CustomerDetailsFormGroupType>(formGroupType: T) => {
  const submittedForms = staticSelector(creditApplicationSelectors.selectSubmittedForms);
  return formGroups[formGroupType].find((_formName) => !submittedForms.includes(_formName));
};

export const useFormSectionHelpers = (
  formGroupType: CustomerDetailsFormGroupType,
  formName: CustomerDetailsSection
) => {
  const submittedForms = useSelector(creditApplicationSelectors.selectSubmittedForms);
  const expandedForm = useSelector(creditApplicationSelectors.selectExpandedFormName);

  const dispatch = useDispatch();

  const getDefaultOpen = () => {
    if (submittedForms.length === 0) {
      // Set as open if it's the first form in the group
      return formGroups[formGroupType][0] === formName;
    }
    if (getFirstNotSubmittedForm(formGroupType) !== formName) {
      return false;
    }

    return expandedForm === formName;
  };

  const getOnChange = () => {
    return (open) => {
      dispatch(setExpandedForm(open ? formName : undefined));
    };
  };

  const onFormSubmitSuccess = () => {
    dispatch(setFormAsSubmitted(formName));
    const firstNotSubmittedForm = getFirstNotSubmittedForm(formGroupType);

    if (!firstNotSubmittedForm) return;

    dispatch(setExpandedForm(firstNotSubmittedForm));
    scrollToSectionTop(firstNotSubmittedForm);
  };

  const onFormInitialValuesChange = async (validateForm: FormikContextType<any>['validateForm']) => {
    const errors = await validateForm();

    if (Object.values(errors).length > 0) {
      dispatch(setFormAsNotSubmitted(formName));
    } else {
      dispatch(setFormAsSubmitted(formName));
    }

    const firstNotSubmittedForm = getFirstNotSubmittedForm(formGroupType);
    dispatch(setExpandedForm(firstNotSubmittedForm));

    if (firstNotSubmittedForm) {
      scrollToSectionTop(firstNotSubmittedForm);
    }
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return {
    formSectionProps: {
      id: formName,
      status: submittedForms.includes(formName) ? ('success' as const) : undefined,
      disabled: !isCreditApplicationDataReady,
      onChange: getOnChange(),
      defaultOpen: isCreditApplicationDataReady ? getDefaultOpen() : false,
    },
    isSectionSubmitted: submittedForms.includes(formName),
    onFormSubmitSuccess,
    onFormInitialValuesChange,
  };
};

export const getPersonalIncomeOfType = <
  T extends DeepPartial<CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse>,
>(
  customerData: T | undefined,
  incomeType: CreditIntegration.Schemas.PersonalIncomeType
) => {
  return (
    customerData?.financialDisclosure?.incomes?.find((income) => income?.incomeType === incomeType)?.amount
      ?.netAmount ?? 0
  );
};

export const getPersonalExpenseOfType = <
  T extends DeepPartial<CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse>,
>(
  customerData: T | undefined,
  expenseType: CreditIntegration.Schemas.PersonalExpenseType
) => {
  return (
    customerData?.financialDisclosure?.expenses?.[0]?.personal?.find((expense) => expense?.expenseType === expenseType)
      ?.amount?.netAmount ?? 0
  );
};

export const getMainEmployment = <T extends CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse>(
  customerData: T
) => {
  return customerData?.employments?.find(
    ({ milesInvolvedInCompanyCategory }) => milesInvolvedInCompanyCategory === 'MAIN_EMPLOYMENT'
  );
};

export const getCustomerRelations = <
  T extends
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse,
>(
  customerData: T,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  role: CreditIntegration.Schemas.CustomerRoleType
) => {
  // TODO: uncomment code below when BFT correctly sends-back role of the relation
  // return customerData.customerRelations?.find(({ role: _role }) => _role === role) as
  //   | (Required<T>['customerRelations'] extends Array<infer U> ? U : undefined)
  //   | undefined;
  return customerData.customerRelations?.[0] as
    | (Required<T>['customerRelations'] extends Array<infer U> ? U : undefined)
    | undefined;
};

export const patchCreditApplicationAndUpdateState = <
  T extends CreditIntegration.Schemas.UpdateCustomerDetailsRequest['customerData'],
>(
  customerData: T,
  isCoCustomer = false
) => {
  const ids = staticSelector(creditApplicationSelectors.selectCreditApplicationIds);
  assertDefinedVoid(ids.quoteId);
  assertDefinedVoid(ids.creditApplicationId);

  const applicantCreditScoreId = isCoCustomer
    ? assertDefined(ids.applicantCreditScoreIdCoCustomer)
    : assertDefined(ids.applicantCreditScoreId);

  return patchCreditApplicationCustomerData(ids.quoteId, ids.creditApplicationId, applicantCreditScoreId, {
    customerData,
  }).then((customerData) => {
    if (isCoCustomer) {
      store.dispatch(setCreditApplicantCustomerDataCoCustomer(customerData));
    } else {
      store.dispatch(setCreditApplicantCustomerData(customerData));
    }
  });
};

/**
 * - Necessary before customer details forms can start to render.
 */
export const initializeCreditApplicationData = async (quoteId: string, creditApplicationId: string) => {
  store.dispatch(loadingActions.setLoading(true));

  try {
    const creditApplicationDetails = await getCreditApplication(creditApplicationId);
    store.dispatch(setCreditApplicationDetails(creditApplicationDetails));
    const applicantCreditScoreId = assertDefined(creditApplicationDetails.customerData?.externalIdentifiers?.[0]?.id);
    const applicantCreditScoreIdCoCustomer =
      creditApplicationDetails.customerData?.customerRelations?.[0].externalIdentifiers?.[0]?.id;
    store.dispatch(setCreditApplicationDetails(creditApplicationDetails));
    const {
      creditApplicationStakeholders: { directorContactId, signatoryContactId },
    } = staticSelector(selectDataStorage);

    const creditApplicantCustomerData = await getCreditApplicationCustomerData(
      quoteId,
      creditApplicationId,
      applicantCreditScoreId
    );

    const milesCustomerId = assertDefined(
      staticSelector(customerSelectors.selectCustomerId) ||
        creditApplicationDetails.customerData?.externalIdentifiers?.find((i) => i?.type === 'MILES_CUSTOMER_ID')?.id
    );
    const customerType = creditApplicantCustomerData.customerData?.customerType;

    if (customerType === CustomerType.LEGAL_ENTITY) {
      const quoteStakeholderContactsResponse = await getQuoteStakeholderContacts(quoteId);
      store.dispatch(setStakeholderContacts(quoteStakeholderContactsResponse.stakeholderContacts ?? []));
      const quoteStakeholderContacts = quoteStakeholderContactsResponse.stakeholderContacts;
      const customerStakeholderContactsResponse = await getCustomerStakeholderContacts(milesCustomerId);
      const customerStakeholderContacts = customerStakeholderContactsResponse.stakeholderContacts;

      const quoteDirectorStakeholderContact = quoteStakeholderContacts?.find(
        (qsc) => qsc.milesStakeholderContactType === 'DIRECTOR'
      );
      const quoteSignatoryStakeholderContact = quoteStakeholderContacts?.find(
        (qsc) => qsc.milesStakeholderContactType === 'SIGNATORY'
      );

      if (!quoteDirectorStakeholderContact) {
        store.dispatch(setShouldLinkDirectorStakeholder(true));
      }

      const directorBasedOnSavedId = customerStakeholderContacts?.find(
        (contact) => contact.milesContactId === directorContactId
      );

      // It can be assumed that customerStakeholderContacts has only one
      // entry with DIRECTOR milesStakeholderContactType.
      const onlyDirectorEntryInContacts = customerStakeholderContacts?.find((contact) =>
        contact.stakeholderContacts?.find((sc) => sc.milesStakeholderContactType === 'DIRECTOR')
      );

      // Both entries should in theory be the same. For now it stays that to not optimize-out
      // some edge-case we might not see.
      const director = directorBasedOnSavedId || onlyDirectorEntryInContacts;

      const signatoryBasedOnSavedId = customerStakeholderContacts?.find(
        (contact) => contact.milesContactId === signatoryContactId
      );

      // Signatory prefill can also be made based on latest entry in the list (latest are at the front)
      const signatoryBasedOnPriority = customerStakeholderContacts?.[0];

      const areStakeholdersTheSamePerson = (
        stakeholderA?: QuoteSchema.Schemas.StakeholderContact | CustomerIntegration.Schemas.StakeholderContactsItem,
        stakeholderB?: QuoteSchema.Schemas.StakeholderContact | CustomerIntegration.Schemas.StakeholderContactsItem
      ) =>
        stakeholderA?.birthName === stakeholderB?.birthName &&
        stakeholderA?.lastName === stakeholderB?.lastName &&
        stakeholderA?.dateOfBirth === stakeholderB?.dateOfBirth;

      // Select signatory stakeholder contact item from customer stakeholders,
      // that is best matching signatory saved on the quote.
      const getMatchingSignatoryStakeholderContactItemBaseOnQuoteContact = ():
        | CustomerIntegration.Schemas.StakeholderContactsItem
        | undefined => {
        const areQuoteStakeholderTheSamePerson = areStakeholdersTheSamePerson(
          quoteDirectorStakeholderContact,
          quoteSignatoryStakeholderContact
        );

        if (areQuoteStakeholderTheSamePerson) return director;

        return customerStakeholderContacts?.find((contact) => {
          const contactIsSignatory = contact.stakeholderContacts?.find(
            (_contact) => _contact.milesStakeholderContactType === 'SIGNATORY'
          );

          return contactIsSignatory && areStakeholdersTheSamePerson(quoteSignatoryStakeholderContact, contact);
        });
      };

      const signatory =
        signatoryBasedOnSavedId ||
        getMatchingSignatoryStakeholderContactItemBaseOnQuoteContact() ||
        signatoryBasedOnPriority;

      const bothStakeholdersAreDefined = director && signatory;

      const isDirectorASignatory = bothStakeholdersAreDefined
        ? areStakeholdersTheSamePerson(director, signatory)
        : true;

      // Temporary patch: placeOfBirthZipCode is missing in customer-integrattion-service response
      if (quoteDirectorStakeholderContact) {
        assertDefined(director).placeOfBirthZipCode = quoteDirectorStakeholderContact.placeOfBirthZipCode;
      }
      // Temporary patch: placeOfBirthZipCode is missing in customer-integrattion-service response
      if (quoteSignatoryStakeholderContact) {
        assertDefined(signatory).placeOfBirthZipCode = quoteSignatoryStakeholderContact.placeOfBirthZipCode;
      }

      if (!quoteSignatoryStakeholderContact && isDirectorASignatory) {
        store.dispatch(setShouldLinkSignatoryStakeholder(true));
      }

      store.dispatch(setIsSignatoryFormActive(!isDirectorASignatory));

      // All stakeholders except currently selected director.
      const availableSignatories = customerStakeholderContacts?.filter(
        (contact) =>
          contact.milesContactId !== director?.milesContactId &&
          contact.stakeholderContacts?.some((c) => c.milesStakeholderContactType === 'SIGNATORY')
      );

      if (availableSignatories?.length) {
        const signatoriesList = availableSignatories.map((signatory) => {
          return {
            role: 'SIGNATORY' as any,
            milesContactId: signatory.milesContactId!,
            milesStakeholderContactId:
              quoteSignatoryStakeholderContact?.milesStakeholderContactId ||
              signatory.stakeholderContacts?.find((sc) => sc.milesStakeholderContactType === 'SIGNATORY')
                ?.milesStakeholderContactId,
            milesEmailId: signatory.contactData?.emails?.at(0)?.milesEmailId,
            milesMobilePhoneNumberId: signatory.contactData?.milesMobilePhoneNumberId,
            formData: getStakeholderInfoDataFormInitialValues(signatory),
          };
        });
        store.dispatch(setAvailableSignatories(signatoriesList));
      }

      if (director) {
        const formData = getStakeholderInfoDataFormInitialValues(director);
        store.dispatch(
          setStakeholderContact({
            role: 'DIRECTOR',
            milesContactId: director.milesContactId!,
            milesStakeholderContactId:
              quoteDirectorStakeholderContact?.milesStakeholderContactId ||
              director.stakeholderContacts?.find((sc) => sc.milesStakeholderContactType === 'DIRECTOR')
                ?.milesStakeholderContactId,
            signatoryStakeholderContactId:
              quoteSignatoryStakeholderContact?.milesStakeholderContactId ||
              director.stakeholderContacts?.find((shc) => shc.milesStakeholderContactType === 'SIGNATORY')
                ?.milesStakeholderContactId,
            milesEmailId: director.contactData?.emails?.at(0)?.milesEmailId,
            milesMobilePhoneNumberId: director.contactData?.milesMobilePhoneNumberId,
            formData,
          })
        );
      }

      if (signatory && !isDirectorASignatory) {
        const formData = getStakeholderInfoDataFormInitialValues(signatory);

        store.dispatch(setIsSignatoryFormActive(true));
        store.dispatch(
          setStakeholderContact({
            role: 'SIGNATORY',
            milesContactId: signatory.milesContactId!,
            milesStakeholderContactId:
              quoteSignatoryStakeholderContact?.milesStakeholderContactId ||
              signatory?.stakeholderContacts?.find((sc) => sc.milesStakeholderContactType === 'SIGNATORY')
                ?.milesStakeholderContactId,
            milesEmailId: signatory?.contactData?.emails?.at(0)?.milesEmailId,
            milesMobilePhoneNumberId: signatory?.contactData?.milesMobilePhoneNumberId,
            formData,
          })
        );
      }
    }

    const creditApplicantCustomerDataCoCustomer = applicantCreditScoreIdCoCustomer
      ? await getCreditApplicationCustomerData(quoteId, creditApplicationId, applicantCreditScoreIdCoCustomer)
      : undefined;

    store.dispatch(setCreditApplicantCustomerData(creditApplicantCustomerData));

    if (creditApplicantCustomerDataCoCustomer) {
      store.dispatch(setCreditApplicantCustomerDataCoCustomer(creditApplicantCustomerDataCoCustomer));
    }

    store.dispatch(setIsCreditApplicationDataReady());
  } finally {
    store.dispatch(loadingActions.setLoading(false));
  }
};

export const getFormNamesToRender = ({
  customerType,
  isCoCustomer,
  isSoleTrader,
}: {
  customerType: CreditIntegration.Schemas.CustomerType;
  isCoCustomer?: boolean;
  isSoleTrader?: boolean;
}) => {
  if (isCoCustomer) return formGroups.coCustomer;
  if (customerType === 'INDIVIDUAL') return formGroups.private;
  if (isSoleTrader) return formGroups.soleTrader;
  return formGroups.business;
};

export type InvolvementInCompanies = {
  oneCustomerHasCompany: BooleanEnum;
  [k: `oneCustomerHasCompany_${number}`]: Exclude<
    CreditIntegration.Schemas.InvolvedInCompanyCategoryType,
    'MAIN_EMPLOYMENT'
  >;
  [k: `sirenWebserviceCall_${number}`]: string;
};

export const employmentsToInvolvementInCompanies = (
  customerData: CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData']
) => {
  return (customerData.employments ?? [])
    .filter(
      (employment) =>
        employment?.milesInvolvedInCompanyCategory && employment.milesInvolvedInCompanyCategory !== 'MAIN_EMPLOYMENT'
    )
    .map((employment, i) => {
      return {
        [`sirenWebserviceCall_${i}`]: employment?.milesInvolvedInCompanySiren,
        [`oneCustomerHasCompany_${i}`]: employment?.milesInvolvedInCompanyCategory,
      };
    })
    .reduce((acc, item) => ({ ...acc, ...item }), {}) as Omit<InvolvementInCompanies, 'oneCustomerHasCompany'>;
};

export const generateCompanyDataFormValidationSchema = (
  t: TFunction,
  values: InvolvementInCompanies
): ObjectSchema<InvolvementInCompanies> => {
  const getPairValidator = (i: number) => ({
    [`sirenWebserviceCall_${i}`]: Yup.string()
      .matches(/^[0-9]+$/, t('validation:invalidCharacter'))
      .required(t('validation:required'))
      .test(
        'doesNotRepeat',
        () => t('validation:invalidData'),
        (value: string) => {
          const fieldName = `sirenWebserviceCall_${i}`;
          const sirenRepeats = Object.keys(values).some((key) => {
            if (!key.startsWith('sirenWebserviceCall_')) return false;
            if (key === fieldName) return false;
            return values[key] === value;
          });
          return !sirenRepeats;
        }
      )
      .min(9, t('validation:minLength', { value: 9 }))
      .max(9, t('validation:maxLength', { value: 9 })),
    [`oneCustomerHasCompany_${i}`]: Yup.string().required(t('validation:required')),
  });

  const neededPairs = Object.keys(values).filter((k) => k.startsWith('sirenWebserviceCall_')).length;

  const pairsValidators = new Array(neededPairs)
    .fill('')
    .reduce((acc, _, i) => ({ ...acc, ...getPairValidator(i) }), {}) as {
    [k: `oneCustomerHasCompany_${number}`]: Yup.AnyObject;
    [k: `sirenWebserviceCall_${number}`]: Yup.AnyObject;
  };

  return Yup.object({
    oneCustomerHasCompany: Yup.string()
      .oneOf(getEnumValues(BooleanEnum), t('validation:invalidData'))
      .required(t('validation:required')),
    ...pairsValidators,
  });
};

export const getEmploymentsForRequestMapper = (
  values: CompanyDataFormType,
  currentCreditApplicationData: CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData']
): CreditIntegration.Schemas.UpdateCustomerDetailsRequest['customerData']['employments'] => {
  /**
   * - Array below will represent new and edited employments.
   * - Edited employments are those with milesemploymentId defined.
   */
  const employments = Object.entries(values)
    .map(([fieldName, fieldValue]): CreditIntegration.Schemas.UpdateIndividualEmploymentDataRequest | null => {
      if (!fieldName.startsWith('sirenWebserviceCall_')) return null;

      const milesInvolvedInCompanySiren = fieldValue;

      const fieldIndex = fieldName.split('_')[1];

      const milesInvolvedInCompanyCategory = values[`oneCustomerHasCompany_${fieldIndex}`];

      // Avoid accidental modification of main eployment
      if (milesInvolvedInCompanyCategory === 'MAIN_EMPLOYMENT') return null;

      const milesemploymentId = currentCreditApplicationData.employments?.find(({ milesInvolvedInCompanySiren }) => {
        return milesInvolvedInCompanySiren === fieldValue;
      })?.milesemploymentId;

      return {
        milesInvolvedInCompanySiren,
        milesInvolvedInCompanyCategory,
        ...(milesemploymentId ? { milesemploymentId } : {}),
      };
    })
    .filter(Boolean) as CreditIntegration.Schemas.UpdateIndividualEmploymentDataRequest[];

  const employmentsToRemove = (currentCreditApplicationData.employments ?? [])
    ?.map((employment) => {
      if (employment.milesInvolvedInCompanyCategory === 'MAIN_EMPLOYMENT') return null;
      if (employments.some((e) => employment.milesemploymentId === e.milesemploymentId)) return null;

      return {
        milesIsDeleted: true,
        ...employment,
      };
    })
    .filter(Boolean) as Array<{ milesemploymentId: string }>;
  return [...employments, ...employmentsToRemove];
};

/**
 * - Get data required to start ident process.
 * - Will be patched to DataStorage and then referenced by ident process handlers on FES.
 */
export const getCustomerIdentData = (
  customerData: CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData'],
  role: 'LEGAL_ENTITY_SOLE_TRADER' | 'INDIVIDUAL' | 'COCONTRACTOR' | 'DIRECTOR' | 'SIGNATORY'
): IdentData => {
  const assertText = 'Ident fields missing.';

  switch (role) {
    case 'INDIVIDUAL':
    case 'COCONTRACTOR': {
      const _ = customerData as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;

      return {
        firstName: assertDefined(_.firstName, assertText),
        lastName: assertDefined(_.lastName, assertText),
        dateOfBirth: assertDefined(_.dateOfBirth, assertText),
        email: assertDefined(_.contactData?.emails?.[0].emailAddress, assertText),
        telephoneNumber: `${assertDefined(_.contactData?.mobilePhoneCountryCode, assertText)}${assertDefined(
          _.contactData?.mobilePhoneNumber,
          assertText
        )}`,
      };
    }
    case 'DIRECTOR': {
      const _ = customerData as
        | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
        | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;
      const stakeholderContacts = staticSelector(customerSelectors.selectStakeholderContacts);
      const executiveStakeholder = stakeholderContacts.DIRECTOR;

      return {
        firstName: assertDefined(executiveStakeholder?.formData.ceoFirstName, assertText),
        lastName: assertDefined(executiveStakeholder?.formData.ceoLastName, assertText),
        dateOfBirth: uiDateToApiDate(assertDefined(executiveStakeholder?.formData.ceoDateOfBirth, assertText))!,
        email: assertDefined(_.contactData?.emails?.[0].emailAddress, assertText),
        // eslint-disable-next-line prettier/prettier
        telephoneNumber: `${assertDefined(_.contactData?.mobilePhoneCountryCode, assertText)}${assertDefined(
          _.contactData?.mobilePhoneNumber,
          assertText
        )}`,
      };
    }
    case 'SIGNATORY': {
      const stakeholderContacts = staticSelector(customerSelectors.selectStakeholderContacts);
      const signatoryStakeholder = stakeholderContacts.SIGNATORY;

      if (!signatoryStakeholder) {
        throw new Error('Error during mapping ident data: Signatory stakeholder not found.');
      }

      const signatoryFormData = signatoryStakeholder.formData;
      const _ = customerData as
        | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
        | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

      const hasContactInfo = (
        formData: any
      ): formData is { email?: string; mobilePhone?: string; mobilePhoneCountryCode?: string } => {
        return 'email' in formData || 'mobilePhone' in formData || 'mobilePhoneCountryCode' in formData;
      };

      return {
        firstName: assertDefined(signatoryFormData?.ceoFirstName, assertText),
        lastName: assertDefined(signatoryFormData?.ceoLastName, assertText),
        dateOfBirth: uiDateToApiDate(assertDefined(signatoryFormData?.ceoDateOfBirth, assertText)),
        email:
          hasContactInfo(signatoryFormData) && signatoryFormData.email
            ? assertDefined(signatoryFormData.email, assertText)
            : assertDefined(_.contactData?.emails?.[0].emailAddress, assertText),
        telephoneNumber:
          hasContactInfo(signatoryFormData) && signatoryFormData.mobilePhoneCountryCode && signatoryFormData.mobilePhone
            ? `${assertDefined(signatoryFormData.mobilePhoneCountryCode, assertText)}${assertDefined(signatoryFormData.mobilePhone, assertText)}`
            : `${assertDefined(_.contactData?.mobilePhoneCountryCode, assertText)}${assertDefined(_.contactData?.mobilePhoneNumber, assertText)}`,
      };
    }
    case 'LEGAL_ENTITY_SOLE_TRADER': {
      const _ = customerData as
        | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
        | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;
      const relations = getCustomerRelations(_, 'CEO');

      return {
        firstName: assertDefined(relations?.firstName, assertText),
        lastName: assertDefined(relations?.lastName, assertText),
        dateOfBirth: assertDefined(relations?.dateOfBirth, assertText),
        email: assertDefined(_.contactData?.emails?.[0].emailAddress, assertText),
        // eslint-disable-next-line prettier/prettier
        telephoneNumber: `${assertDefined(_.contactData?.mobilePhoneCountryCode, assertText)}${assertDefined(
          _.contactData?.mobilePhoneNumber,
          assertText
        )}`,
      };
    }
    default:
      throw new Error('Error during mapping ident data: Unrecognized customer type.');
  }
};

export const extractDashboardIdentData = (
  customerData: CreditIntegration.Schemas.GetApplicantDetailsCustomerData['customerData'],
  quoteStakeholderContacts?: QuoteSchema.Schemas.StakeholderContact[]
): IdentData => {
  const assertText = 'Ident fields missing.';

  if (customerData.customerType === 'INDIVIDUAL') {
    const privateData = customerData as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;
    return {
      firstName: assertDefined(privateData.firstName, assertText),
      lastName: assertDefined(privateData.lastName, assertText),
      dateOfBirth: assertDefined(privateData.dateOfBirth, assertText),
      email: assertDefined(privateData.contactData?.emails?.[0]?.emailAddress, assertText),
      telephoneNumber: assertDefined(
        privateData.contactData?.telephoneNumber ?? privateData.contactData?.mobilePhoneNumber,
        assertText
      ),
    };
  }

  const legalEntityData = customerData as
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const stakeholders = quoteStakeholderContacts ?? [];
  const directorContact = stakeholders.find((sc) => sc.milesStakeholderContactType === 'DIRECTOR');
  const signatoryContact = stakeholders.find((sc) => sc.milesStakeholderContactType === 'SIGNATORY');
  const hasDistinctSignatory =
    !!signatoryContact && signatoryContact.milesStakeholderContactId !== directorContact?.milesStakeholderContactId;

  if (hasDistinctSignatory) {
    const countryCode =
      signatoryContact!.contactData?.mobilePhoneCountryCode ?? legalEntityData.contactData?.mobilePhoneCountryCode;
    const phoneNumber =
      signatoryContact!.contactData?.mobilePhoneNumber ?? legalEntityData.contactData?.mobilePhoneNumber;
    return {
      firstName: assertDefined(signatoryContact!.firstName, assertText),
      lastName: assertDefined(signatoryContact!.lastName, assertText),
      dateOfBirth: assertDefined(signatoryContact!.dateOfBirth, assertText),
      email: assertDefined(
        signatoryContact!.contactData?.emails?.[0]?.emailAddress ??
          legalEntityData.contactData?.emails?.[0]?.emailAddress,
        assertText
      ),
      telephoneNumber: assertDefined(
        countryCode && phoneNumber ? `${countryCode}${phoneNumber}` : undefined,
        assertText
      ),
    };
  }

  const relation = legalEntityData.customerRelations?.[0];
  return {
    firstName: assertDefined(relation?.firstName, assertText),
    lastName: assertDefined(relation?.lastName, assertText),
    dateOfBirth: assertDefined(relation?.dateOfBirth, assertText),
    email: assertDefined(legalEntityData.contactData?.emails?.[0]?.emailAddress, assertText),
    telephoneNumber: assertDefined(
      legalEntityData.contactData?.telephoneNumber ?? legalEntityData.contactData?.mobilePhoneNumber,
      assertText
    ),
  };
};

export const appendDataStorageIdToPathname = (id: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set(KnownSearchParams.cid, id);
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
};

export const createInitialCreditApplication = async () => {
  const { saveAndContinueMobilePhoneNumber } = store.getState().dataStorageNew;
  const creditApplicationAlreadyStarted = saveAndContinueMobilePhoneNumber && getTransactionId();

  // This case is when we are on S&C restored state
  if (creditApplicationAlreadyStarted) return;

  const {
    payload: { id: loadingId },
  } = store.dispatch(loadingActions.startContextualLoading());

  const quote = staticSelector(quoteSelectors.selectQuote);

  const productName = assertDefined(quote.financialProduct?.offers?.[0]?.productName);
  const quoteId = assertDefined(staticSelector(quoteSelectors.selectQuoteId));
  const lastVisitedLocation = `${routes.form}/4`;
  const milesCustomerId = staticSelector(customerSelectors.selectCustomerId);
  const dataStoragePayload: CreateCreditApplicationRequest = {
    dataStorageData: {
      quoteId,
      milesCustomerId,
      productName,
      lastVisitedLocation,
    },
  };

  await waitForMiles();

  const { dataStorageObject, creditApplication } = await createCreditApplication(quoteId, dataStoragePayload);

  appendDataStorageIdToPathname(dataStorageObject.id);

  store.dispatch(setCreditApplicationDetails(creditApplication));
  store.dispatch(setDataStorageNew(dataStorageObject.data.custom));

  Analytics.push(applicationFormEvent);
  store.dispatch(loadingActions.endContextualLoading(loadingId));
};

export const getDisabledFields = (
  data: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse,
  isExistingClient: boolean
) => ({
  nationality: Boolean(data.nationality),
  birthCountry: isExistingClient || Boolean(data.countryOfBirth),
  zipCodeOfBirth: Boolean(data.placeOfBirthZipCode),
  placeOfBirth: Boolean(data.placeOfBirth),
});
